import {
  Component,
  ElementRef,
  PLATFORM_ID,
  Renderer2,
  signal,
  ViewChild,
  OnInit,
  OnDestroy,
  inject,
  DOCUMENT,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Header } from './components/core/header/header';
import { Footer } from './components/core/footer/footer';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { filter, map, mergeMap } from 'rxjs';
import { Meta, Title } from '@angular/platform-browser';
import { CanonicalService } from './services/canonical-service/canonical-service';
import { ImageSrcService } from './services/image-src-service/image-src-service';

@Component({
  selector: 'app-root',
  imports: [CommonModule, RouterOutlet, Header, Footer],
  templateUrl: './app.html',
  styleUrl: './app.less',
})
export class App implements OnInit, OnDestroy {
  @ViewChild('routeHost', { static: true }) routeHost!: ElementRef<HTMLElement>;
  @ViewChild('scroller', { static: true }) scroller!: ElementRef<HTMLElement>;

  showScrollTop = signal(false);

  private r = inject(Renderer2);
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private metaService = inject(Meta);
  private titleService = inject(Title);
  private _document = inject(DOCUMENT);
  private canonicalService = inject(CanonicalService);
  private imageSrcService = inject(ImageSrcService);

  constructor() {
    if (this.isBrowser && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }

  onActivate() {
    const host = this.routeHost.nativeElement;

    this.r.removeClass(host, 'route-enter');
    void host.offsetHeight;
    this.r.addClass(host, 'route-enter');
    setTimeout(() => this.r.removeClass(host, 'route-enter'), 220);

    if (this.isBrowser && this.scroller) {
      const el = this.scroller.nativeElement;
      requestAnimationFrame(() => {
        el.scrollTop = 0;
        el.scrollLeft = 0;
      });
    }
  }

  ngOnInit() {
    if (this.isBrowser) {
      const el = this.scroller.nativeElement;
      el.addEventListener('scroll', this.onScroll, { passive: true });
    }

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        mergeMap((route) => route.data),
      )
      .subscribe((event) => {
        const description =
          event['description'] ||
          'Psicóloga sanitaria en Santander especializada en ansiedad y gestión emocional. Terapia presencial y online.';

        const title = this.titleService.getTitle();
        const url = this._document.location.href;
        const image = 'https://anapellonpsicologa.com/ana_share.jpg';
        const imageAlt = 'Ana Pellón Mendoza, psicóloga sanitaria en Santander';
        const robots = event['robots'] ?? 'index,follow';
        const robotsContent = robots.includes('noindex')
          ? robots
          : `${robots},max-image-preview:large`;

        this.metaService.updateTag({ name: 'description', content: description });
        this.metaService.updateTag({ name: 'robots', content: robots });

        // OPEN GRAPH (WhatsApp, Facebook, LinkedIn)
        this.metaService.updateTag({ property: 'og:title', content: title });
        this.metaService.updateTag({ property: 'og:description', content: description });
        this.metaService.updateTag({ property: 'og:image', content: image });
        this.metaService.updateTag({ property: 'og:image:alt', content: imageAlt });
        this.metaService.updateTag({ property: 'og:url', content: url });
        this.metaService.updateTag({ property: 'og:type', content: 'website' });
        this.metaService.updateTag({ property: 'og:site_name', content: 'Ana Pellón Psicóloga' });
        this.metaService.updateTag({ property: 'og:locale', content: 'es_ES' });

        // TWITTER CARDS
        this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
        this.metaService.updateTag({ name: 'twitter:title', content: title });
        this.metaService.updateTag({ name: 'twitter:description', content: description });
        this.metaService.updateTag({ name: 'twitter:image', content: image });
        this.metaService.updateTag({ name: 'twitter:image:alt', content: imageAlt });

        this.metaService.updateTag({ name: 'robots', content: robotsContent });
      });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      const canonicalUrl = `https://anapellonpsicologa.com${this.router.url.split('?')[0]}`;

      this.canonicalService.updateCanonicalUrl(canonicalUrl);
    });

    this.imageSrcService.update('https://anapellonpsicologa.com/ana_pellon_psicologa_santander.webp');
  }

  ngOnDestroy() {
    if (this.isBrowser) {
      this.scroller.nativeElement.removeEventListener('scroll', this.onScroll);
    }
  }

  private onScroll = (event: Event) => {
    const target = event.target as HTMLElement;

    this.showScrollTop.set(target.scrollTop > 150);
  };

  scrollToTop() {
    if (!this.scroller) return;
    const el = this.scroller.nativeElement;
    el.scrollTo({ top: 0, behavior: 'smooth' });
    this.showScrollTop.set(false);
  }
}
